import {
  generalAdabyronBehaviour,
  makeUndefinedValueNull,
  USER_TYPES,
} from '../../utils/adaByron';
import { MethodType, Context } from '../../utils/types';
import { normalizeDisagregateProduct } from './normalizers';

export enum PATHS {
  GET_CATALOG_SECTIONS = 'catalogo/secciones',
  GET_CATALOG_SUBSECTION = 'catalogo/subseccion',
  GET_PRODUCT_OFFERING = 'catalogo/oferta',
  GET_PRODUCT_OFFERING_ATTACHMENTS = 'catalogo/documentos',
  DISGREGATE_PRODUCT = 'catalogo/v3/modulos/disgregar',
  COVERAGE = 'catalogo/cobertura',
  GET_COMMERCIAL_ZONE = 'catalogo/zona',
  GET_MODULOS_EQUIVALENCIAS = 'catalogo/v2/modulos/equivalencias',
  GET_BIFURCAR = 'catalogo/v3/modulos/bifurcar',
  IS_R2R_AVAILABLE_TO_PURCHASE = 'catalogo/renttorent/disponibilidad',
  GET_RENT_TO_RENT = 'catalogo/renttorent',
  GET_R2R_COMPATIBLE_DATA = 'catalogo/renttorent/compatibilidades',
  GET_INSURANCE_DATA = 'catalogo/modalidades/seguro'
}

export const getCatalogSections = (
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CATALOG_SECTIONS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'CATALOG.GET_CATALOG_SECTIONS',
    mockedInfo: 'getCatalogSections',
    methodType: MethodType.GET,
  });
};

export const getCatalogSubsection = (
  data: { sectionId: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { sectionId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CATALOG_SUBSECTION,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'CATALOG.GET_CATALOG_SUBSECTION',
    queryParams: [{ param: 'id', value: sectionId }],
    mockedInfo: 'getCatalogSubsection',
    methodType: MethodType.GET,
  });
};

export const getProductOffering = (
  data: { idPO: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { idPO } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_OFFERING,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CATALOG.GET_PRODUCT_OFFERING',
    queryParams: [{ param: 'id', value: idPO }],
    mockedInfo: 'getProductOffering',
    methodType: MethodType.GET,
  });
};

export const getProductOfferingAttachments = (
  data: { idPO: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { idPO } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_OFFERING_ATTACHMENTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'CATALOG.GET_PRODUCT_OFFERING_ATTACHMENTS',
    queryParams: [{ param: 'productOffering', value: idPO }],
    mockedInfo: 'getProductOfferingAttachments',
    methodType: MethodType.GET,
  });
};

export const getProductOfferingAllInfo = (
  data: {
    characteristics?: Array<{
      id: string;
      valueId: string;
    }>;
    extras?: Array<{
      characteristics?: Array<{
        id: string;
        valueId: string;
      }>;
      id: string;
      promotionId?: string;
    }>;
    idPO: string;
    promotionId?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { characteristics, extras, idPO, promotionId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_OFFERING,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CATALOG.GET_PRODUCT_OFFERING_ALL_INFO',
    mockedInfo: 'getProductOfferingAllInfo',
    methodType: MethodType.POST,
    requestObject: {
      constraints: makeUndefinedValueNull({
        characteristics,
        extras,
        id: idPO,
        promotionId,
      }),
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const disagregateProduct = (
  data: {
    productOfferings: Array<unknown>;
    customNormalizer?: (response: any) => any;
    maximizeRetrievedData?: boolean;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productOfferings, customNormalizer, maximizeRetrievedData } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.DISGREGATE_PRODUCT,
    userTypes: [USER_TYPES.OPEN, USER_TYPES.CLIENT],
    eventName: 'CATALOG.DISAGREGATE_PRODUCT',
    mockedInfo: 'disagregateProduct',
    queryParams: [
      {
        param: 'clave',
        value:
          'MIIDAzCCAesCAQAwgb0xCzAJBgNVBAYTAkVTMQ8wDQYDVQQIDAZNQURSSUQxDzAN',
      },
    ],
    methodType: MethodType.POST,
    requestObject: {
      productOfferings,
    },
    maximizeRetrievedData: maximizeRetrievedData,
    onSuccessNormalizer: customNormalizer || normalizeDisagregateProduct,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getCoverage = (
  data: {
    geographicSiteId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { geographicSiteId } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.COVERAGE,
    userTypes: [USER_TYPES.OPEN],
    eventName: 'CATALOG.GET_COVERAGE',
    mockedInfo: 'coverage',
    queryParams: [{ param: 'geographicSiteId', value: geographicSiteId }],
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    methodType: MethodType.GET,
  });
};

export const getCommercialZone = (
  data: { pc: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { pc } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_COMMERCIAL_ZONE,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CATALOG.GET_COMMERCIAL_ZONE',
    mockedInfo: 'getCommercialZone',
    methodType: MethodType.GET,
    queryParams: [
      {
        param: 'codigoPostal',
        value: pc,
      },
    ],
  });
};

export const getModulosEquivalencias = (
  data: { po: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { po } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_MODULOS_EQUIVALENCIAS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CATALOG.GET_MODULOS_EQUIVALENCIAS',
    mockedInfo: 'getModulosEquivalencias',
    methodType: MethodType.GET,
    queryParams: [
      {
        param: 'productOfferingId',
        value: po,
      },
      {
        param: 'clave',
        value:
          'MIIDAzCCAesCAQAwgb0xCzAJBgNVBAYTAkVTMQ8wDQYDVQQIDAZNQURSSUQxDzAN',
      },
    ],
  });
};

export const getBifurcar = (
  data: { id: string }[],
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_BIFURCAR,
    userTypes: [USER_TYPES.OPEN, USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'CATALOG.GET_BIFURCAR',
    mockedInfo: 'bifurcar',
    queryParams: [
      {
        param: 'clave',
        value:
          'MIIDAzCCAesCAQAwgb0xCzAJBgNVBAYTAkVTMQ8wDQYDVQQIDAZNQURSSUQxDzAN',
      },
    ],
    requestObject: {
      productOfferings: data,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    methodType: MethodType.POST,
  });
};
export const isR2RAvailableToPurchase = (
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.IS_R2R_AVAILABLE_TO_PURCHASE,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CATALOG.IS_R2R_AVAILABLE_TO_PURCHASE',
    mockedInfo: 'isR2RAvailableToPurchase',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getRentToRent = (
  data: { type: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { type } = data;
  const queryParams = [];
  if (type) {
    queryParams.push({
      param: 'type',
      value: type.toString(),
    });
  }
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_RENT_TO_RENT,
    userTypes: [USER_TYPES.OPEN],
    eventName: 'ADABYRON.RENT_TO_RENT',
    mockedInfo: 'getRentToRent',
    methodType: MethodType.GET,
    queryParams: queryParams,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getR2rCompatibleData = (
  data: {
    type: string;
    businessZone: string;
    productOffering: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const queryParams = [];
  const { type, businessZone, productOffering } = data;
  if (type) {
    queryParams.push({
      param: 'type',
      value: type.toString(),
    });
  }
  if (productOffering) {
    queryParams.push({
      param: 'productOfferingConvergente',
      value: productOffering.toString(),
    });
  }
  if (businessZone) {
    queryParams.push({
      param: 'businessZone',
      value: businessZone.toString(),
    });
  }
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_R2R_COMPATIBLE_DATA,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'ADABYRON.R2R_COMPATIBLE',
    mockedInfo: 'getR2rCompatibleData',
    methodType: MethodType.GET,
    queryParams: queryParams,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 2,
  });
};

export const getInsuranceData = (
  data: {
    idMemory: string;
    idSeguroPO: string;
    insuranceType: string;
    productOfferingId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    idMemory,
    idSeguroPO,
    insuranceType,
    productOfferingId,
  } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_INSURANCE_DATA,
    userTypes: [USER_TYPES.OPEN, USER_TYPES.CLIENT],
    eventName: 'CATALOG.GET_INSURANCE_DATA',
    mockedInfo: 'getInsuranceData',
    methodType: MethodType.POST,
    requestObject: {
      idMemory,
      idSeguroPO: [ idSeguroPO ],
      insuranceType,
      productOfferingId,
    },
    onSuccessNormalizer: null,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};
